import React from 'react';
import { mobilePlotArea, mobileLayerStock, mobileStandTotals } from '../../../utils/mobileCalculations';
import { calculateSKS, calculateOtpadObsh, calculateOtpadTek } from './utils';
import { calculateDamagePercentage as calculateForm5DamagePercentage } from '../Form5/utils';

const CalculationsSection = ({
  displayData,
  calculateTotalsByNasazhdeniya,
  calculationsScrollRef,
  calculationsScrollPosition,
  handleCalculationsSliderChange,
  handleCalculationsScroll,
  hasForm4Data = false,
  form4Data = null,
  form4SavedModalData = {},
  form5Data = null,
  handbooks = [],
  independentForm2Data = null,
  form2Data = null,
  dataType = 'to',
  selectedForm45 = 4,
  form1Data = null
}) => {
  
  // Функция для расчета доли поврежденных деревьев из Form5 для яруса
  const calculateForm5DamageForYarus = (yarusNumber) => {
    if (!form5Data || !form5Data.data || selectedForm45 !== 5) {
      return '0.00';
    }
    
    const dataArray = Array.isArray(form5Data.data) ? form5Data.data : [];
    const yarusData = dataArray.filter(item => 
      (item.yarus === yarusNumber.toString() || item.yarus === yarusNumber)
    );
    
    if (yarusData.length === 0) {
      return '0.00';
    }
    
    // Вычисляем сумму по той же формуле, что в Form5
    const total = yarusData.reduce((sum, item) => {
      const damagePercentage = calculateForm5DamagePercentage(item);
      const dolap = parseFloat(item.dolap || 0) || 0;
      return sum + (damagePercentage * dolap / 10);
    }, 0);
    
    return total.toFixed(2);
  };
  
  // Функция для расчета итоговой доли поврежденных деревьев из Form5
  // Использует ту же логику, что и calculateTotalsByNasazhdeniya в Form5
  const calculateForm5TotalDamage = () => selectedForm45 === 5
    ? mobileStandTotals(form5Data, independentForm2Data, mobilePlotArea(form1Data, dataType), 5).dolapovr
    : '0.00';

  // Функция для расчета obj (сумма zapassirga * ploshadVydel + zapassuh + zapaszahl) для яруса
  const calculateObjForYarus = (yarusNumber) => {
    // Получаем данные из Form2
    let yarusyDependent = [];
    
    // Сначала проверяем independentForm2Data
    if (independentForm2Data && independentForm2Data.yarusy_dependent) {
      yarusyDependent = independentForm2Data.yarusy_dependent;
    } 
    // Если нет, проверяем form2Data
    else if (form2Data && form2Data[dataType] && form2Data[dataType].yarusy_dependent) {
      yarusyDependent = form2Data[dataType].yarusy_dependent;
    }
    
    if (!yarusyDependent || yarusyDependent.length === 0) {
      return 0;
    }
    
    const area = mobilePlotArea(form1Data, dataType);

    // Фильтруем по ярусу
    const yarusRows = yarusyDependent.filter(item => 
      (item.yarus === yarusNumber.toString() || item.yarus === yarusNumber)
    );
    
    if (yarusRows.length === 0) {
      return 0;
    }
    
    const obj = yarusRows.reduce((sum, tier) => sum + mobileLayerStock(tier, area), 0);

    return obj;
  };
  
  // Функция для расчета итоговой строки "Итог по насаждению"
  const calculateCausesForTotal = (useSpecificDola = false) => {
    if (!hasForm4Data || !form4Data || !form4Data.data) {
      return '';
    }
    
    // Рассчитываем obj для каждого яруса
    const obj1 = calculateObjForYarus(1);
    const obj2 = calculateObjForYarus(2);
    const obj3 = calculateObjForYarus(3);
    const totalObj = obj1 + obj2 + obj3;
    
    // Если totalObj равен 0, но есть данные, используем простую сумму (как для ярусов)
    const useSimpleSum = totalObj === 0;
    
    // Получаем справочники
    const causesHandbook = handbooks.find(h => h.id === 10);
    const signsHandbook = handbooks.find(h => h.id === 9);
    
    // Собираем все причины из всех ярусов
    const causesMap = new Map(); // key: causeCode, value: { priznCodes: Set, resSum: number }
    
    // Обрабатываем каждый ярус
    [1, 2, 3].forEach(yarusNumber => {
      const objYarus = yarusNumber === 1 ? obj1 : (yarusNumber === 2 ? obj2 : obj3);
      
      // Получаем все строки данного яруса из Form4
      const yarusRows = form4Data.data.filter(item => 
        (item.yarus === yarusNumber.toString() || item.yarus === yarusNumber)
      );
      
      if (yarusRows.length === 0) {
        return;
      }
      
      yarusRows.forEach((row) => {
        const rowId = row.id;
        const dolap = row.dolap;
        
        // Если доля породы "+" или не число - рассматриваем как 0
        const dolapValue = (dolap === '+' || isNaN(parseFloat(dolap))) ? 0 : parseFloat(dolap) || 0;
        
        // Получаем данные модального окна для этой строки
        const modalDataForRow = form4SavedModalData[rowId];
        if (!modalDataForRow || !Array.isArray(modalDataForRow) || modalDataForRow.length === 0) {
          return;
        }
        
        modalDataForRow.forEach((modalItem) => {
          if (!modalItem.prichina) {
            return;
          }
          
          // Выбираем долю в зависимости от параметра
          let dola = 0;
          if (useSpecificDola) {
            dola = parseFloat(modalItem.specificDolaPrizn) || 0;
          } else {
            if (!modalItem.dolaprizn) {
              return;
            }
            dola = parseFloat(modalItem.dolaprizn) || 0;
          }
          
          // Получаем code причины
          let causeCode = modalItem.prichina;
          if (causesHandbook && causesHandbook.references) {
            const cause = causesHandbook.references.find(
              r => r.id.toString() === String(modalItem.prichina)
            );
            if (cause && cause.code) {
              causeCode = cause.code.toString();
            }
          }
          
          // Вычисляем РЕЗ для яруса = (dola * dolap) / 10
          const resYarus = (dola * dolapValue) / 10;
          
          // Вычисляем итоговое значение
          let resTotal = 0;
          if (useSimpleSum) {
            // Если totalObj равен 0, используем простую сумму (как для ярусов)
            resTotal = resYarus;
          } else {
            // Иначе используем взвешенное среднее: (РЕЗ_яруса * obj_яруса) / (obj_яруса1 + obj_яруса2 + obj_яруса3)
            resTotal = (resYarus * objYarus) / totalObj;
          }
          
          // Получаем code признаков
          const priznCodesSet = new Set();
          
          if (modalItem.prizn) {
            let priznCode = '';
            if (signsHandbook && signsHandbook.references) {
              const sign = signsHandbook.references.find(
                r => r.id.toString() === String(modalItem.prizn)
              );
              if (sign && sign.code) {
                priznCode = sign.code.toString();
              } else {
                priznCode = modalItem.prizn;
              }
            } else {
              priznCode = modalItem.prizn;
            }
            if (priznCode) {
              priznCodesSet.add(priznCode);
            }
          }
          
          if (modalItem.priznList && Array.isArray(modalItem.priznList) && modalItem.priznList.length > 0) {
            modalItem.priznList.forEach(priznItem => {
              let code = '';
              if (typeof priznItem === 'string') {
                if (signsHandbook && signsHandbook.references) {
                  const sign = signsHandbook.references.find(
                    r => r.id.toString() === String(priznItem)
                  );
                  code = sign && sign.code ? sign.code.toString() : priznItem;
                } else {
                  code = priznItem;
                }
              } else if (priznItem && priznItem.code) {
                code = priznItem.code.toString();
              } else if (priznItem && priznItem.id) {
                if (signsHandbook && signsHandbook.references) {
                  const sign = signsHandbook.references.find(
                    r => r.id.toString() === String(priznItem.id)
                  );
                  code = sign && sign.code ? sign.code.toString() : priznItem.id.toString();
                } else {
                  code = priznItem.id.toString();
                }
              }
              if (code) {
                priznCodesSet.add(code);
              }
            });
          }
          
          // Добавляем или обновляем причину в карте
          if (!causesMap.has(causeCode)) {
            causesMap.set(causeCode, {
              priznCodes: new Set(),
              resSum: 0
            });
          }
          
          const causeData = causesMap.get(causeCode);
          // Добавляем все признаки в Set (без дубликатов)
          priznCodesSet.forEach(code => {
            causeData.priznCodes.add(code);
          });
          // Суммируем результаты для повторяющихся причин
          causeData.resSum += resTotal;
        });
      });
    });
    
    // Форматируем результат
    const causesList = [];
    causesMap.forEach((causeData, causeCode) => {
      const priznStr = causeData.priznCodes.size > 0 
        ? `(${Array.from(causeData.priznCodes).join(', ')})` 
        : '';
      const formattedCause = `${causeCode}${priznStr}/${causeData.resSum.toFixed(1)}`;
      causesList.push(formattedCause);
    });
    
    return causesList.join('; ');
  };
  
  // Функция для расчета доли повреждённых деревьев по причинам для яруса
  const calculateCausesForYarus = (yarusNumber, useSpecificDola = false) => {
    if (!hasForm4Data || !form4Data || !form4Data.data) {
      return '';
    }
    
    // Получаем все строки данного яруса из Form4
    // Учитываем все строки, включая те, где dolap === '+'
    const yarusRows = form4Data.data.filter(item => 
      (item.yarus === yarusNumber.toString() || item.yarus === yarusNumber)
    );
    
    if (yarusRows.length === 0) {
      return '';
    }
    
    // Получаем справочник причин (ID 10) для получения code
    const causesHandbook = handbooks.find(h => h.id === 10);
    const signsHandbook = handbooks.find(h => h.id === 9);
    
    // Собираем все причины из всех строк яруса
    const causesMap = new Map(); // key: causeCode, value: { priznCodes: Set, resSum: number }
    
    yarusRows.forEach((row) => {
      const rowId = row.id;
      const dolap = row.dolap;
      
      // Если доля породы "+" или не число - рассматриваем как 0
      const dolapValue = (dolap === '+' || isNaN(parseFloat(dolap))) ? 0 : parseFloat(dolap) || 0;
      
      // Получаем данные модального окна для этой строки
      const modalDataForRow = form4SavedModalData[rowId];
      if (!modalDataForRow || !Array.isArray(modalDataForRow) || modalDataForRow.length === 0) {
        return;
      }
      
      modalDataForRow.forEach((modalItem) => {
        if (!modalItem.prichina) {
          return;
        }
        
        // Выбираем долю в зависимости от параметра
        let dola = 0;
        if (useSpecificDola) {
          // Для правого столбца используем специальную долю, если её нет - 0
          dola = parseFloat(modalItem.specificDolaPrizn) || 0;
        } else {
          // Для левого столбца используем обычную долю
          if (!modalItem.dolaprizn) {
            return; // Пропускаем записи без доли
          }
          dola = parseFloat(modalItem.dolaprizn) || 0;
        }
        
        // Получаем code причины
        let causeCode = modalItem.prichina; // По умолчанию используем ID
        if (causesHandbook && causesHandbook.references) {
          const cause = causesHandbook.references.find(
            r => r.id.toString() === String(modalItem.prichina)
          );
          if (cause && cause.code) {
            causeCode = cause.code.toString();
          }
        }
        
        // Вычисляем РЕЗ = (dola * dolap) / 10
        const res = (dola * dolapValue) / 10;
        
        // Получаем code признаков (может быть несколько)
        const priznCodesSet = new Set();
        
        // Если есть prizn (одиночный признак)
        if (modalItem.prizn) {
          let priznCode = '';
          if (signsHandbook && signsHandbook.references) {
            const sign = signsHandbook.references.find(
              r => r.id.toString() === String(modalItem.prizn)
            );
            if (sign && sign.code) {
              priznCode = sign.code.toString();
            } else {
              priznCode = modalItem.prizn;
            }
          } else {
            priznCode = modalItem.prizn;
          }
          if (priznCode) {
            priznCodesSet.add(priznCode);
          }
        }
        
        // Если есть priznList (массив признаков)
        if (modalItem.priznList && Array.isArray(modalItem.priznList) && modalItem.priznList.length > 0) {
          modalItem.priznList.forEach(priznItem => {
            let code = '';
            if (typeof priznItem === 'string') {
              if (signsHandbook && signsHandbook.references) {
                const sign = signsHandbook.references.find(
                  r => r.id.toString() === String(priznItem)
                );
                code = sign && sign.code ? sign.code.toString() : priznItem;
              } else {
                code = priznItem;
              }
            } else if (priznItem && priznItem.code) {
              code = priznItem.code.toString();
            } else if (priznItem && priznItem.id) {
              if (signsHandbook && signsHandbook.references) {
                const sign = signsHandbook.references.find(
                  r => r.id.toString() === String(priznItem.id)
                );
                code = sign && sign.code ? sign.code.toString() : priznItem.id.toString();
              } else {
                code = priznItem.id.toString();
              }
            }
            if (code) {
              priznCodesSet.add(code);
            }
          });
        }
        
        // Добавляем или обновляем причину в карте
        if (!causesMap.has(causeCode)) {
          causesMap.set(causeCode, {
            priznCodes: new Set(),
            resSum: 0
          });
        }
        
        const causeData = causesMap.get(causeCode);
        // Добавляем все признаки в Set (без дубликатов)
        priznCodesSet.forEach(code => {
          causeData.priznCodes.add(code);
        });
        causeData.resSum += res;
      });
    });
    
    // Форматируем результат
    const causesList = [];
    causesMap.forEach((causeData, causeCode) => {
      const priznStr = causeData.priznCodes.size > 0 
        ? `(${Array.from(causeData.priznCodes).join(', ')})` 
        : '';
      const formattedCause = `${causeCode}${priznStr}/${causeData.resSum.toFixed(1)}`;
      causesList.push(formattedCause);
    });
    
    return causesList.join('; ');
  };
  // Функция для расчета итогов по ярусу
  const calculateYarusTotals = (yarusNumber, column) => {
    const yarusData = displayData.data.filter(item => item.yarus === yarusNumber.toString() || item.yarus === yarusNumber);
    if (yarusData.length === 0) return 0;
    
    const total = yarusData.reduce((sum, item) => {
      let value = 0;
      
      if (column === 'sks') {
        value = calculateSKS(item);
      } else if (column === 'otpad_obsh') {
        value = calculateOtpadObsh(item);
      } else if (column === 'otpad_tek') {
        value = calculateOtpadTek(item);
      } else {
        value = Number(item[column]) || 0;
      }
      
      const dolya = Number(item.dolya) || 0;
      return sum + (value * dolya / 10);
    }, 0);
    
    return total;
  };

  // Функция для рендеринга ячейки итога по ярусу
  const renderYarusTotalCell = (yarusNumber, column) => {
    const yarusData = displayData.data.filter(item => item.yarus === yarusNumber.toString() || item.yarus === yarusNumber);
    if (yarusData.length === 0) return '0.00';
    
    const total = yarusData.reduce((sum, item) => {
      let value = 0;
      
      if (column === 'sks') {
        value = calculateSKS(item);
      } else if (column === 'otpad_obsh') {
        value = calculateOtpadObsh(item);
      } else if (column === 'otpad_tek') {
        value = calculateOtpadTek(item);
      } else {
        value = Number(item[column]) || 0;
      }
      
      const dolya = Number(item.dolya) || 0;
      return sum + (value * dolya / 10);
    }, 0);
    
    return total.toFixed(2);
  };

  const totals = calculateTotalsByNasazhdeniya ? calculateTotalsByNasazhdeniya() : {
    sks: '0.00',
    otpad_obsh: '0.00',
    otpad_tek: '0.00'
  };

  return (
    <div style={{ marginTop: '20px' }}>
      <h3 style={{ 
        margin: '0 0 15px 0', 
        fontSize: '16px', 
        fontWeight: '600',
        color: '#333'
      }}>
        Расчеты
      </h3>
      
      <div 
        ref={calculationsScrollRef}
        onScroll={handleCalculationsScroll}
        style={{
          overflowX: 'auto',
          border: '1px solid #dee2e6',
          borderRadius: '6px',
          backgroundColor: '#fff'
        }}>
        <table style={{
          width: '100%',
          minWidth: (selectedForm45 === 4 && hasForm4Data) ? '1000px' : (selectedForm45 === 5 ? '400px' : (selectedForm45 === 4 ? '600px' : '400px')),
          borderCollapse: 'collapse',
          fontSize: '12px'
        }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa' }}>
              <th style={{ 
                minWidth: '150px', 
                width: '150px',
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '600'
              }}>
                Наименование
              </th>
              {selectedForm45 === 4 && (
                <>
                  <th style={{ 
                    minWidth: '100px', 
                    width: '100px',
                    padding: '8px',
                    border: '1px solid #dee2e6',
                    textAlign: 'center',
                    fontWeight: '600'
                  }}>
                    СКС
                  </th>
                  <th style={{ 
                    minWidth: '120px', 
                    width: '120px',
                    padding: '8px',
                    border: '1px solid #dee2e6',
                    textAlign: 'center',
                    fontWeight: '600'
                  }}>
                    Отпад общий
                  </th>
                  <th style={{ 
                    minWidth: '120px', 
                    width: '120px',
                    padding: '8px',
                    border: '1px solid #dee2e6',
                    textAlign: 'center',
                    fontWeight: '600'
                  }}>
                    Отпад текущий
                  </th>
                </>
              )}
              {(selectedForm45 === 4 && hasForm4Data) || selectedForm45 === 5 ? (
                <th style={{ 
                  minWidth: '200px', 
                  width: '200px',
                  padding: '8px',
                  border: '1px solid #dee2e6',
                  textAlign: 'center',
                  fontWeight: '600'
                }}>
                  {selectedForm45 === 5
                    ? 'Повреждение крон'
                    : 'Доля повреждённых деревьев по причинам повреждения, % от запаса'}
                </th>
              ) : null}
              {selectedForm45 === 4 && hasForm4Data && (
                <th style={{ 
                  minWidth: '200px', 
                  width: '200px',
                  padding: '8px',
                  border: '1px solid #dee2e6',
                  textAlign: 'center',
                  fontWeight: '600'
                }}>
                  Доля повреждённых деревьев по причинам повреждения для определения очага, % от запаса
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {/* Итог по 1-му ярусу - всегда показываем */}
            <tr>
              <td style={{ 
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'left',
                fontWeight: '500'
              }}>
                Итог по 1-му ярусу
              </td>
              {selectedForm45 === 4 && (
                <>
                  <td style={{ 
                    padding: '8px',
                    border: '1px solid #dee2e6',
                    textAlign: 'center',
                    fontWeight: '500'
                  }}>
                    {renderYarusTotalCell(1, 'sks')}
                  </td>
                  <td style={{ 
                    padding: '8px',
                    border: '1px solid #dee2e6',
                    textAlign: 'center',
                    fontWeight: '500'
                  }}>
                    {renderYarusTotalCell(1, 'otpad_obsh')}
                  </td>
                  <td style={{ 
                    padding: '8px',
                    border: '1px solid #dee2e6',
                    textAlign: 'center',
                    fontWeight: '500'
                  }}>
                    {renderYarusTotalCell(1, 'otpad_tek')}
                  </td>
                </>
              )}
              {(selectedForm45 === 4 && hasForm4Data) || selectedForm45 === 5 ? (
                <td style={{ 
                  padding: '8px',
                  border: '1px solid #dee2e6',
                  textAlign: 'center',
                  fontWeight: '500',
                  fontSize: '11px',
                  lineHeight: '1.3',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word'
                }}>
                  {selectedForm45 === 4 ? calculateCausesForYarus(1, false) : calculateForm5DamageForYarus(1)}
                </td>
              ) : null}
              {selectedForm45 === 4 && hasForm4Data && (
                <td style={{ 
                  padding: '8px',
                  border: '1px solid #dee2e6',
                  textAlign: 'center',
                  fontWeight: '500',
                  fontSize: '11px',
                  lineHeight: '1.3',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word'
                }}>
                  {calculateCausesForYarus(1, true)}
                </td>
              )}
            </tr>

            {/* Итог по 2-му ярусу - показываем только если есть данные по 2-му ярусу */}
            {(() => {
              const secondYarusData = displayData.data.filter(item => item.yarus === '2' || item.yarus === 2);
              const hasSecondYarus = secondYarusData.length > 0;
              
              if (hasSecondYarus) {
                return (
                  <tr>
                    <td style={{ 
                      padding: '8px',
                      border: '1px solid #dee2e6',
                      textAlign: 'left',
                      fontWeight: '500'
                    }}>
                      Итог по 2-му ярусу
                    </td>
                    {selectedForm45 === 4 && (
                      <>
                        <td style={{ 
                          padding: '8px',
                          border: '1px solid #dee2e6',
                          textAlign: 'center',
                          fontWeight: '500'
                        }}>
                          {renderYarusTotalCell(2, 'sks')}
                        </td>
                        <td style={{ 
                          padding: '8px',
                          border: '1px solid #dee2e6',
                          textAlign: 'center',
                          fontWeight: '500'
                        }}>
                          {renderYarusTotalCell(2, 'otpad_obsh')}
                        </td>
                        <td style={{ 
                          padding: '8px',
                          border: '1px solid #dee2e6',
                          textAlign: 'center',
                          fontWeight: '500'
                        }}>
                          {renderYarusTotalCell(2, 'otpad_tek')}
                        </td>
                      </>
                    )}
                    {(selectedForm45 === 4 && hasForm4Data) || selectedForm45 === 5 ? (
                      <td style={{ 
                        padding: '8px',
                        border: '1px solid #dee2e6',
                        textAlign: 'center',
                        fontWeight: '500',
                        fontSize: '11px',
                        lineHeight: '1.3',
                        whiteSpace: 'normal',
                        wordWrap: 'break-word'
                      }}>
                        {selectedForm45 === 4 ? calculateCausesForYarus(2, false) : calculateForm5DamageForYarus(2)}
                      </td>
                    ) : null}
                    {selectedForm45 === 4 && hasForm4Data && (
                      <td style={{ 
                        padding: '8px',
                        border: '1px solid #dee2e6',
                        textAlign: 'center',
                        fontWeight: '500',
                        fontSize: '11px',
                        lineHeight: '1.3',
                        whiteSpace: 'normal',
                        wordWrap: 'break-word'
                      }}>
                        {calculateCausesForYarus(2, true)}
                      </td>
                    )}
                  </tr>
                );
              }
              return null;
            })()}

            {/* Итог по 3-му ярусу - показываем только если есть данные по 3-му ярусу */}
            {(() => {
              const thirdYarusData = displayData.data.filter(item => item.yarus === '3' || item.yarus === 3);
              const hasThirdYarus = thirdYarusData.length > 0;
              
              if (hasThirdYarus) {
                return (
                  <tr>
                    <td style={{ 
                      padding: '8px',
                      border: '1px solid #dee2e6',
                      textAlign: 'left',
                      fontWeight: '500'
                    }}>
                      Итог по 3-му ярусу
                    </td>
                    {selectedForm45 === 4 && (
                      <>
                        <td style={{ 
                          padding: '8px',
                          border: '1px solid #dee2e6',
                          textAlign: 'center',
                          fontWeight: '500'
                        }}>
                          {renderYarusTotalCell(3, 'sks')}
                        </td>
                        <td style={{ 
                          padding: '8px',
                          border: '1px solid #dee2e6',
                          textAlign: 'center',
                          fontWeight: '500'
                        }}>
                          {renderYarusTotalCell(3, 'otpad_obsh')}
                        </td>
                        <td style={{ 
                          padding: '8px',
                          border: '1px solid #dee2e6',
                          textAlign: 'center',
                          fontWeight: '500'
                        }}>
                          {renderYarusTotalCell(3, 'otpad_tek')}
                        </td>
                      </>
                    )}
                    {(selectedForm45 === 4 && hasForm4Data) || selectedForm45 === 5 ? (
                      <td style={{ 
                        padding: '8px',
                        border: '1px solid #dee2e6',
                        textAlign: 'center',
                        fontWeight: '500',
                        fontSize: '11px',
                        lineHeight: '1.3',
                        whiteSpace: 'normal',
                        wordWrap: 'break-word'
                      }}>
                        {selectedForm45 === 4 ? calculateCausesForYarus(3, false) : calculateForm5DamageForYarus(3)}
                      </td>
                    ) : null}
                    {selectedForm45 === 4 && hasForm4Data && (
                      <td style={{ 
                        padding: '8px',
                        border: '1px solid #dee2e6',
                        textAlign: 'center',
                        fontWeight: '500',
                        fontSize: '11px',
                        lineHeight: '1.3',
                        whiteSpace: 'normal',
                        wordWrap: 'break-word'
                      }}>
                        {calculateCausesForYarus(3, true)}
                      </td>
                    )}
                  </tr>
                );
              }
              return null;
            })()}

            {/* Итог по насаждению - всегда показываем, суммируем все данные */}
            <tr style={{ backgroundColor: '#f8f9fa' }}>
              <td style={{ 
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'left',
                fontWeight: '600',
                fontSize: '13px'
              }}>
                Итог по насаждению
              </td>
              {selectedForm45 === 4 && (
                <>
                  <td style={{ 
                    padding: '8px',
                    border: '1px solid #dee2e6',
                    textAlign: 'center',
                    fontWeight: '600',
                    fontSize: '13px'
                  }}>
                    {totals.sks || '0.00'}
                  </td>
                  <td style={{ 
                    padding: '8px',
                    border: '1px solid #dee2e6',
                    textAlign: 'center',
                    fontWeight: '600',
                    fontSize: '13px'
                  }}>
                    {totals.otpad_obsh || '0.00'}
                  </td>
                  <td style={{ 
                    padding: '8px',
                    border: '1px solid #dee2e6',
                    textAlign: 'center',
                    fontWeight: '600',
                    fontSize: '13px'
                  }}>
                    {totals.otpad_tek || '0.00'}
                  </td>
                </>
              )}
              {(selectedForm45 === 4 && hasForm4Data) || selectedForm45 === 5 ? (
                <td style={{ 
                  padding: '8px',
                  border: '1px solid #dee2e6',
                  textAlign: 'center',
                  fontWeight: '600',
                  fontSize: '13px',
                  background: '#f8f9fa',
                  fontSize: '11px',
                  lineHeight: '1.3',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word'
                }}>
                  {selectedForm45 === 4 ? calculateCausesForTotal(false) : calculateForm5TotalDamage()}
                </td>
              ) : null}
              {selectedForm45 === 4 && hasForm4Data && (
                <td style={{ 
                  padding: '8px',
                  border: '1px solid #dee2e6',
                  textAlign: 'center',
                  fontWeight: '600',
                  fontSize: '13px',
                  background: '#f8f9fa',
                  fontSize: '11px',
                  lineHeight: '1.3',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word'
                }}>
                  {calculateCausesForTotal(true)}
                </td>
              )}
            </tr>
          </tbody>
        </table>
      </div>
      
      {/* Слайдбар для прокрутки таблицы расчетов */}
      <div style={{
        marginTop: '10px',
        padding: '10px',
        backgroundColor: '#f8f9fa',
        border: '1px solid #e9ecef',
        borderRadius: '6px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
      }}>
        <span style={{ 
          fontSize: '12px', 
          color: '#6c757d',
          minWidth: '60px'
        }}>
          Прокрутка:
        </span>
        <input
          type="range"
          min="0"
          max="100"
          value={calculationsScrollPosition}
          onChange={handleCalculationsSliderChange}
          className="table-scroll-slider"
          style={{
            flex: 1
          }}
        />
        <span style={{ 
          fontSize: '12px', 
          color: '#6c757d',
          minWidth: '30px',
          textAlign: 'right'
        }}>
          {Math.round(calculationsScrollPosition)}%
        </span>
      </div>
    </div>
  );
};

export default CalculationsSection;

